import type { ReactElement } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { OutputButton } from './output-button.js';
import type { DeviceEgressSelectorType } from '#~/statequery/selectors/device-egress.js';
import { SourcesList } from './output.js';
import { OutputFooter } from './output-footer.js';

type OutputButtonState = 'stopping' | 'starting' | 'in-progress' | 'idle';

interface NDIOutputHeaderProps {
  isStopping: boolean;
  isStarting: boolean;
  isOutputting: boolean;
  hasChanges: boolean;
  onSafeStop: () => void;
  onSave: () => void;
  videoSources: DeviceEgressSelectorType[];
  audioSources: DeviceEgressSelectorType[];
  selected: Record<string, boolean>;
  onSelection: (deviceId: string) => void;
}

const getOutputButtonState = ({
  isStopping,
  isStarting,
  isOutputting
}: Pick<NDIOutputHeaderProps, 'isStopping' | 'isStarting' | 'isOutputting'>): OutputButtonState => {
  if (isStopping) return 'stopping';
  if (isStarting) return 'starting';
  if (isOutputting) return 'in-progress';
  return 'idle';
};

/**
 * Header component for NDI Output section that displays the title and control button
 */
export const NDI = ({
  isStopping,
  isStarting,
  isOutputting,
  hasChanges,
  onSafeStop,
  onSave,
  videoSources,
  audioSources,
  selected,
  onSelection
}: NDIOutputHeaderProps): ReactElement => {
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  /** Measure the absolutely-positioned footer so we can pad the content area to prevent overlap. */
  useLayoutEffect(() => {
    if (footerRef.current !== null) {
      setFooterHeight(footerRef.current.offsetHeight + 12);
    }
  }, []);

  const buttonState = getOutputButtonState({ isStopping, isStarting, isOutputting });
  const isButtonDisabled = isStopping || isStarting || (!isOutputting && !hasChanges);
  const buttonAction = isOutputting ? onSafeStop : onSave;

  return (
    <>
      <div
        className='flex h-full w-full flex-col gap-32 p-8'
        style={{ paddingBottom: footerHeight }}
      >
        <div className='flex items-start justify-between'>
          <div className='mt-4 text-sm text-muted'>Select the sources you would like to output</div>
          <OutputButton state={buttonState} disabled={isButtonDisabled} onLabel={buttonAction} />
        </div>

        {/* Sources selection section */}
        <div className='border-t-1 border-base-50' />
        <div>
          <h2>Select sources</h2>
          <div className='text-sm text-muted'>
            Select the sources you would like to include in output.
          </div>
        </div>

        {/* Video Sources */}
        <div className='flex gap-x-40'>
          <div className='flex flex-1 flex-col gap-12'>
            <h3>Video sources ({videoSources.length})</h3>
            {videoSources.length === 0 ? (
              <p className='text-muted'>No video sources are connected.</p>
            ) : (
              <SourcesList
                type='video'
                sources={videoSources}
                selected={selected}
                onSelection={onSelection}
              />
            )}
          </div>
          <div className='flex flex-1 flex-col gap-12'>
            <h3>Audio sources ({audioSources.length})</h3>
            {audioSources.length === 0 ? (
              <p className='text-muted'>No audio sources are connected.</p>
            ) : (
              <SourcesList
                type='audio'
                sources={audioSources}
                selected={selected}
                onSelection={onSelection}
              />
            )}
          </div>
        </div>
      </div>
      <div ref={footerRef} className='absolute bottom-0 left-0 right-0'>
        <OutputFooter
          isOutputting={isOutputting}
          isStarting={isStarting}
          isStopping={isStopping}
          hasChanges={hasChanges}
          onSave={onSave}
        />
      </div>
    </>
  );
};
